<template>
  <div class="file-drag-drop">
    <form
      ref="fileForm"
      class="file-drag-drop__form"
    >
      <div
        v-if="files.length"
        class="file-drag-drop__listing"
      >
        <div
          v-for="(file, index) in files"
          :key="index"
          class="file-drag-drop__file"
        >
          <v-btn
            class="file-drag-drop__file-icon"
            tile
            fab
            dark
            x-small
            depressed
            color="white"
          >
            <v-icon
              color="primary"
              small
            >
              mdi-file
            </v-icon>
          </v-btn>
          <div class="file-drag-drop__file-name">
            {{ file.data.name }}
          </div>
          <v-btn
            v-if="file.status === 'pending'"
            icon
            x-small
            color="red"
            class="ml-auto"
            @click="removeFile"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-progress-circular
            v-if="file.status === 'processing'"
            indeterminate
            color="amber"
            size="24"
          />
          <v-icon
            v-if="file.status === 'uploaded'"
            medium
            color="green"
          >
            mdi-check
          </v-icon>
          <v-icon
            v-if="file.status === 'error'"
            medium
            color="red"
          >
            mdi-alert-circle-outline
          </v-icon>
        </div>
      </div>

      <div
        v-else
        class="file-drag-drop__drop-zone"
      >
        <label
          class="file-drag-drop__input-label"
          for="attachments"
        >
          <img
            class="file-drag-drop__add-btn"
            src="@/assets/img/icon-upload-file.svg"
          >
          <div class="file-drag-drop__chose-text">
            Wybierz plik
          </div>
          <div class="file-drag-drop__description">
            Maksymalny rozmiar pliku 1 GB.<br>
            <span v-if="fileTypesRestricted">
              Niedozwolone formaty to {{ filtersOptions.excludedOutputFormats.join(', ') }}
            </span>
          </div>
          <input
            id="attachments"
            class="file-drag-drop__input"
            type="file"
            size="100"
            :multiple="!single"
            name="attachments"
            @change="addFiles"
          >
        </label>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  props: {
    value: {
      type: Array,
      required: true,
    },
    processing: {
      type: Boolean,
      default: false,
    },
    single: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dragAndDropCapable: false,
      errors: [],
    };
  },

  computed: {
    ...mapState('filters', [
      'filtersOptions',
    ]),
    ...mapGetters('auth', [
      'checkUserPermissions',
    ]),
    fileTypesRestricted() {
      return !this.checkUserPermissions(['admin_director', 'chief']);
    },

    files: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
  },

  mounted() {
    this.dragAndDropCapable = this.determineDragAndDropCapable();

    if (this.dragAndDropCapable) {
      ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach((evt) => {
        this.$refs.fileForm.addEventListener(evt, (e) => {
          e.preventDefault();
          e.stopPropagation();
        }, false);
      });

      this.$refs.fileForm.addEventListener('drop', (e) => {
        this.addFiles(e);
      });
    }
  },

  beforeDestroy() {
    ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach((evt) => {
      this.$refs.fileForm.removeEventListener(evt, (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });
  },

  methods: {
    determineDragAndDropCapable() {
      const div = document.createElement('div');

      return (('draggable' in div) || ('ondragstart' in div && 'ondrop' in div)) && 'FormData' in window && 'FileReader' in window;
    },

    addFiles(e) {
      const fileList = e.target.files || e.dataTransfer.files;
      const errors = [];

      const megabyteInBytes = 1048576;
      const limitInMegabytes = 1024;

      Array.from(fileList).forEach((file) => {
        const fileName = file.name;
        const format = fileName.substr(fileName.lastIndexOf('.') + 1).toLowerCase();

        if (file.size / megabyteInBytes > limitInMegabytes) {
          errors.push(`Plik ${fileName} jest za duży`);
        } else if (
          this.fileTypesRestricted && this.filtersOptions.excludedOutputFormats.indexOf(format) > -1
        ) {
          errors.push(`Plik ${fileName} ma nieprawidłowy format`);
        } else {
          const fileData = {
            status: 'pending',
            data: file,
          };
          this.files.push(fileData);
        }
      });

      if (errors.length) this.$emit('error', errors);
    },

    removeFile(index) {
      this.files.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.file-drag-drop {
  background: #FCFCFC 0% 0% no-repeat padding-box;
  border: 1px dashed #E2E6EA;
  border-radius: 4px;

  &__form {
    min-height: 114px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }

  &__drop-zone {
    font-weight: 500;
    text-align: center;
  }

  &__input-label {
    cursor: pointer;
    height: 100%;
    width: 100%;
    display: block;
  }

  &__add-btn {
    height: 32px;
    width: auto;
    margin-bottom: 4px;
  }

  &__file {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
    color: $darken;
    margin-bottom: 10px;
  }

  &__chose-text {
    color: $primary;
    font-size: 12px;
    line-height: 24px;
  }

  &__description {
    color: $light-grey;
    font-size: 10px;
    line-height: 15px;
  }

  &__input {
    display: none;
  }

  &__listing {
    width: 100%;
  }

  &__file-icon {
    border-radius: 6px;
    margin-right: 16px;
    height: 24px;
    width: 24px;
    cursor: default;
  }

  &__file-name {
    width: 100%;
    margin-right: 16px;
    overflow: hidden;
  }
}
</style>
